import React, {useCallback, useRef, useEffect} from 'react'
import {Modal, ModalHeader, ModalBody, Input, InputGroup, InputGroupText, Spinner} from 'reactstrap'
import {ReactComponent as IconSearch} from '../../../../../assets/icons/search.svg'
import {ReactComponent as ComputerIcon} from '../../../../../assets/icons/computer.svg'
import {isBackofficeEnv} from '../../../../../helpers/isBackoffceEnv'
import {hasFeatureTag} from '../../../../../helpers/settings'
import {useListDesignLibrary} from '../../hooks/queries/useListDesignLibrary'
import InfiniteScroll from '../InfiniteScroll'
import EmptyListImg from '../../../../../assets/images/empty-list.png'

const SORT_OPTIONS = [
    {value: 'desc', label: 'Latest - oldest'},
    {value: 'asc', label: 'Oldest - latest'},
]

/**
 * UI-only modal for adding an image (device upload or library selection).
 * Emits onPickFile(file) and onSelectLibraryImage(assetId) — no upload, no editor mutations.
 *
 * @param {{ isOpen: boolean, toggle: () => void, onPickFile: (file: File) => void, onSelectLibraryImage: (assetId: string) => void }} props
 */
function AddImageModal({isOpen, toggle, onPickFile, onSelectLibraryImage}) {
    const fileInputRef = useRef(null)
    const {list, total, hasMore, loading, setFilters, fetchNextPage} = useListDesignLibrary()

    const handleUploadFromDevice = useCallback(
        (e) => {
            const file = e?.target?.files?.[0]
            if (!file) return
            onPickFile(file)
            toggle()
            e.target.value = ''
        },
        [onPickFile, toggle],
    )

    const handleSelectFromLibrary = useCallback(
        (imageId) => {
            onSelectLibraryImage(imageId)
            toggle()
        },
        [onSelectLibraryImage, toggle],
    )

    const searchTimeoutRef = useRef(null)
    const handleSearch = useCallback(
        (e) => {
            const value = e?.target?.value?.trim() ?? ''
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
            searchTimeoutRef.current = setTimeout(() => setFilters({name: value}), 500)
        },
        [setFilters],
    )
    useEffect(
        () => () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
        },
        [],
    )

    const handleSortChange = useCallback(
        (e) => {
            const value = e?.target?.value ?? 'desc'
            setFilters({sort_order: value})
        },
        [setFilters],
    )

    const showDrive = isBackofficeEnv() && hasFeatureTag('upload_image_google_drive')
    const showDropbox = isBackofficeEnv() && hasFeatureTag('update_image_dropbox')

    return (
        <Modal isOpen={isOpen} toggle={toggle} className="CreateCustomShape__AddImageModal ModalUploadArtwork">
            <ModalHeader toggle={toggle}>Add image</ModalHeader>
            <ModalBody>
                <div className="AddImageModal__UploadSource">
                    <label className="AddImageModal__UploadSourceLabel">Upload from:</label>
                    <div className="AddImageModal__UploadSourceButtons">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                            className="d-none"
                            id="add-image-device"
                            onChange={handleUploadFromDevice}
                        />
                        <label
                            htmlFor="add-image-device"
                            className="AddImageModal__SourceBtn AddImageModal__SourceBtn--device"
                        >
                            <ComputerIcon className="AddImageModal__SourceBtnIcon" />
                            <span>Your device</span>
                        </label>
                        {showDrive && (
                            <button
                                type="button"
                                className="AddImageModal__SourceBtn AddImageModal__SourceBtn--disabled"
                                disabled
                            >
                                Google Drive
                            </button>
                        )}
                        {showDropbox && (
                            <button
                                type="button"
                                className="AddImageModal__SourceBtn AddImageModal__SourceBtn--disabled"
                                disabled
                            >
                                Dropbox
                            </button>
                        )}
                    </div>
                </div>

                <div className="AddImageModal__Library">
                    <div className="AddImageModal__LibraryControls">
                        <h3 className="AddImageModal__LibraryTitle">Files in Library ({total})</h3>
                        <div className="AddImageModal__LibraryFilters">
                            <InputGroup className="AddImageModal__SearchGroup">
                                <InputGroupText className="AddImageModal__SearchIconWrap">
                                    <IconSearch />
                                </InputGroupText>
                                <Input
                                    name="name"
                                    placeholder="Search..."
                                    onChange={handleSearch}
                                    className="AddImageModal__SearchInput"
                                />
                            </InputGroup>
                            <div className="AddImageModal__SortWrap">
                                <label className="AddImageModal__SortLabel">Sort by:</label>
                                <Input
                                    type="select"
                                    name="sort_order"
                                    onChange={handleSortChange}
                                    className="AddImageModal__SortSelect"
                                >
                                    {SORT_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </Input>
                            </div>
                        </div>
                    </div>

                    {loading && list.length === 0 ? (
                        <div className="AddImageModal__Loading">
                            <Spinner color="primary" />
                        </div>
                    ) : list.length === 0 ? (
                        <div className="AddImageModal__Empty">
                            <img src={EmptyListImg} alt="" className="AddImageModal__EmptyImg" />
                            <p className="AddImageModal__EmptyText">
                                <strong className="AddImageModal__EmptyTitle">
                                    No images yet? Let&apos;s fix that!
                                </strong>
                                Upload your first image now to kickstart your library
                            </p>
                        </div>
                    ) : (
                        <InfiniteScroll
                            hasMore={hasMore}
                            onFetchMore={fetchNextPage}
                            loader={
                                hasMore && (
                                    <div className="AddImageModal__ListLoader">
                                        <Spinner size="sm" color="primary" />
                                    </div>
                                )
                            }
                            className="AddImageModal__ListScroll"
                        >
                            <div className="AddImageModal__Grid">
                                {list.map((asset) => (
                                    <div
                                        key={asset._id}
                                        className="AddImageModal__Card"
                                        onClick={() => handleSelectFromLibrary(asset._id)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSelectFromLibrary(asset._id)}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <div className="AddImageModal__CardThumb">
                                            <img
                                                src={asset.thumb_url || asset.src || asset.origin_url}
                                                alt={asset.name}
                                            />
                                        </div>
                                        <p className="AddImageModal__CardName" title={asset.name}>
                                            {asset.name}
                                        </p>
                                        {typeof asset.size === 'number' && (
                                            <p className="AddImageModal__CardMeta">
                                                Size: {(asset.size / 1024 / 1024).toFixed(2)} Mb
                                            </p>
                                        )}
                                        {asset.width != null && asset.height != null && (
                                            <p className="AddImageModal__CardMeta">
                                                Resolution: {asset.width} x {asset.height}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </InfiniteScroll>
                    )}
                </div>
            </ModalBody>
        </Modal>
    )
}

export default AddImageModal
